import React, { useEffect, useState, useRef } from "react";
import ReusableTable from "../components/ReusableTable";
import { getApiUrl } from "../utils/api";
import { BASE_URL } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const Ordermanagement = () => {
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterDate, setFilterDate] = useState("");
  const invoiceRef = useRef(null);

  const UserId = "66abc123def4567890abcd12";

  const fetchOrders = async () => {
    try {
      const res = await fetch(getApiUrl("order-success"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success) {
        const sorted = data.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setOrders(sorted);
        setFiltered(sorted);
      } else toast.error(data.message || "Failed to load orders");
    } catch {
      toast.error("Server error, please try again.");
    }
  };

  

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    let result = [...orders];
    if (search) {
      result = result.filter((o) =>
        (o?.userId?.name || "guest")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }
    if (filterDate) {
      result = result.filter(
        (o) => new Date(o.createdAt).toISOString().slice(0, 10) === filterDate
      );
    }
    setFiltered(result);
  }, [search, orders, filterDate]);

  const handleShipChange = async (orderId, status) => {
    try {
      const res = await fetch(getApiUrl(`order/ship-status/${orderId}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shipStatus: status }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Shipping status updated");
        fetchOrders();
      } else toast.error("Failed to update");
    } catch {
      toast.error("Server error");
    }
  };



const [productInfoMap, setProductInfoMap] = useState({});
const fetchProductInfo = async (productId) => {
  try {
    const res = await fetch(getApiUrl("product-basic-info"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ productId }),
    });

    const data = await res.json();

    if (data.success) {
      let image = data.data.image || "";

      if (image && !image.startsWith("http")) {
        image = `${BASE_URL.replace(/\/$/, "")}/${image.replace(/^\/+/, "")}`;
      }

      return {
        subcategory: data.data.subcategory || "-",
        image,
        name: data.data.productName || "-",
      };
    }
  } catch (err) {
    console.error("Product info fetch error", err);
  }

  return { subcategory: "-", image: "", name: "-" };
};

useEffect(() => {
  const loadProductInfos = async () => {
    const map = {};

    for (const order of orders) {
      for (const p of order.products) {
        const productId =
          typeof p.productId === "object" ? p.productId._id : p.productId;

        if (!map[productId]) {
          map[productId] = await fetchProductInfo(productId);
        }
      }
    }

    setProductInfoMap(map);
  };

  if (orders.length) loadProductInfos();
}, [orders]);

const getProductInfo = (orderProduct) => {
  const productId =
    typeof orderProduct.productId === "object"
      ? orderProduct.productId._id
      : orderProduct.productId;

  return (
    productInfoMap[productId] || {
      subcategory: "-",
      image: "",
      name: orderProduct.name || "-",
    }
  );
};


  const columns = [
    {
      name: "Si No",
      selector: (row, index) => filtered.length - index,
    },

    {
      name: "Product Name",
      cell: (row) => (
        <ul className="list-unstyled mb-0">
          {row.products.map((p, idx) => (
            <li key={idx}>{p.name}</li>
          ))}
        </ul>
      ),
    },
    {
      name: "Subcategory",
      cell: (row) => (
        <ul className="list-unstyled mb-0">
          {row.products.map((p, idx) => (
            <li key={idx}>{getProductInfo(p).subcategory}</li>
          ))}
        </ul>
      ),
    },
    {
      name: "Image",
      cell: (row) => (
        <div className="d-flex gap-1 flex-wrap">
          {row.products.map((p, idx) => {
            const { image, name } = getProductInfo(p);
            if (!image) return null;

            return (
              <img
                key={idx}
                src={image}
                alt={name}
                width="50"
                height="50"
                style={{ objectFit: "cover", borderRadius: "6px" }}
              />
            );
          })}
        </div>
      ),
    },
    {
      name: "Quantity",
      selector: (row) => {
        const totalQty = row.products.reduce((sum, p) => sum + (p.qty || 0), 0);
        return totalQty;
      },
    },

    {
      name: "Size",
      cell: (row) => (
        <ul className="list-unstyled mb-0">
          {row.products.map((p, idx) => (
            <li key={idx}>{p.size}</li>
          ))}
        </ul>
      ),
    },
    { name: "Customer", selector: (row) => row?.userId?.name || "Guest" },
    { name: "Amount", selector: (row) => `₹${row.amount}` },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`badge ${
            row.paymentStatus === "SUCCESS"
              ? "bg-success"
              : "bg-warning text-dark"
          }`}
        >
          {row.paymentStatus}
        </span>
      ),
    },
    {
      name: "Shipping",
      cell: (row) => (
        <select
          className="form-select form-select-sm"
          value={row.shipStatus || "processing"}
          onChange={(e) => handleShipChange(row._id, e.target.value)}
        >
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
        </select>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          className="btn btn-info btn-sm"
          onClick={() => setSelectedOrder(row)}
        >
          View
        </button>
      ),
    },
  ];

  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const renderOrderHTML = (order) => `
<style>
* { box-sizing:border-box; font-family:Arial,Helvetica,sans-serif; font-weight:700;}
body{margin:0;padding:0;}
.page{width:100%;min-height:100mm;display:flex;flex-direction:column;align-items:center;padding:10px 0;}
.container{width:95%;padding:16px;border:1px solid #000;display:flex;flex-direction:column;background:#fff;}
.logo{text-align:center;margin-bottom:18px;}
.logo img{max-height:110px;}
h2{margin:6px 0;font-size:30px;text-transform:uppercase;text-align:center;}
.section{font-size:18px;border-top:2px solid #000;padding-top:10px;margin-bottom:16px;}
.section-title{font-size:20px;margin-bottom:6px;font-weight:900;text-decoration:underline;}
table{width:100%;border-collapse:collapse;font-size:18px;}
th,td{border:1px solid #000;padding:10px 8px;}
td img{width:50px;height:50px;object-fit:cover;}
.total{font-size:26px;text-align:center;border-top:2px solid #000;padding-top:12px;margin-bottom:24px;}
.thank-you{text-align:center;font-size:22px;font-weight:900;border-top:1px solid #000;padding-top:10px;}
</style>

<div class="page">
<div class="container">

<div class="logo">
<img src="${
    window.location.origin
  }/image/boutique.jpg" crossorigin="anonymous" />
<h2>BSK Boutique</h2>
</div>

<div class="section">
<div class="section-title">Order Details</div>
Order Date: ${formatDate(order.createdAt)}<br/>
Print Date: ${formatDate(new Date())}
</div>

<div class="section">
<div class="section-title">From / To</div>
<strong>FROM:</strong><br/>BSK Boutique, Rajapalayam, Tamil Nadu<br/><br/>
<strong>TO:</strong><br/>${order?.userId?.name || "Guest"}<br/>
${order?.address?.address || ""}<br/>
${order?.address?.district || ""}, ${order?.address?.state || ""}<br/>
Pin: ${order?.address?.pincode || "-"}<br/>
Ph: ${order?.userId?.phone || "-"}
</div>

<div class="section">
<div class="section-title">Products</div>
<table>
<thead>
<tr>
<th>Item</th><th>Qty</th><th>Size</th><th>₹</th>
</tr>
</thead>
<tbody>
${order.products
  .map(
    (p) => `
<tr>
<td>${p.name}</td>
<td style="text-align:center;">${p.qty}</td>
<td style="text-align:center;">${p.size}</td>
<td>${p.price}</td>
</tr>
`
  )
  .join("")}
</tbody>
</table>
</div>

<div class="total">
TOTAL: ₹${order.amount}
</div>

<div class="thank-you">
Thank you for purchasing from BSK Boutique!
</div>

</div>
</div>
`;

  const printOrDownloadBulk = async (ordersList, isPdf = false) => {
    if (!ordersList.length) return toast.error("No orders found");
    if (!isPdf) {
      const w = window.open("", "_blank");
      w.document.write(`<html><body>`);
      ordersList.forEach((o) => w.document.write(renderOrderHTML(o)));
      w.document.write("</body></html>");
      w.document.close();
      w.print();
      return;
    }

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    for (let i = 0; i < ordersList.length; i++) {
      const div = document.createElement("div");
      div.innerHTML = renderOrderHTML(ordersList[i]);
      div.style.width = "900px";
      document.body.appendChild(div);

      const canvas = await html2canvas(div, { scale: 1.6 });
      const img = canvas.toDataURL("image/jpeg", 0.72);
      const width = pdf.internal.pageSize.getWidth();
      const height = (canvas.height * width) / canvas.width;
      pdf.addImage(img, "JPEG", 0, 0, width, height);
      document.body.removeChild(div);
      if (i < ordersList.length - 1) pdf.addPage();
    }
    pdf.save(`Orders-${filterDate || "all"}.pdf`);
  };

  const getFilteredOrders = (status = null) => {
    let list = [...filtered];
    if (status) list = list.filter((o) => o.shipStatus === status);
    return list;
  };

  const [rangeStart, setRangeStart] = useState(1);
  const [rangeEnd, setRangeEnd] = useState(5);

  const printOrDownloadRange = (isPdf = false) => {
    const start = Math.max(1, Number(rangeStart)) - 1; // convert to index
    const end = Math.min(filtered.length, Number(rangeEnd)); // exclusive
    const ordersToPrint = filtered.slice(start, end);

    if (!ordersToPrint.length) return toast.error("No orders in this range");

    printOrDownloadBulk(ordersToPrint, isPdf);
  };

  return (
    <div className="container mt-4">
      <ToastContainer />
      <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
        <h3 className="fw-bold">Order Management</h3>
        <div className="d-flex gap-2">
          <input
            type="date"
            className="form-control"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Search customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="input-group" style={{ maxWidth: "150px" }}>
          <span className="input-group-text">From</span>
          <input
            type="number"
            className="form-control"
            placeholder="1"
            value={rangeStart}
            onChange={(e) => setRangeStart(e.target.value)}
            min="1"
            max={filtered.length}
          />
        </div>

        <div className="input-group" style={{ maxWidth: "150px" }}>
          <span className="input-group-text">To</span>
          <input
            type="number"
            className="form-control"
            placeholder="5"
            value={rangeEnd}
            onChange={(e) => setRangeEnd(e.target.value)}
            min="1"
            max={filtered.length}
          />
        </div>

        <button
          className="btn btn-primary"
          onClick={() => printOrDownloadRange(false)}
        >
          Print
        </button>

        <button
          className="btn btn-outline-primary"
          onClick={() => printOrDownloadRange(true)}
        >
          PDF
        </button>
      </div>

      <div className="mb-3 d-flex flex-wrap gap-2">
        <button
          className="btn btn-primary"
          onClick={() => printOrDownloadBulk(getFilteredOrders(), false)}
        >
          Print All
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => printOrDownloadBulk(getFilteredOrders(), true)}
        >
          PDF All
        </button>

        <button
          className="btn btn-warning"
          onClick={() =>
            printOrDownloadBulk(getFilteredOrders("processing"), false)
          }
        >
          Print Processing
        </button>
        <button
          className="btn btn-outline-warning"
          onClick={() =>
            printOrDownloadBulk(getFilteredOrders("processing"), true)
          }
        >
          PDF Processing
        </button>

        <button
          className="btn btn-success"
          onClick={() =>
            printOrDownloadBulk(getFilteredOrders("shipped"), false)
          }
        >
          Print Shipped
        </button>
        <button
          className="btn btn-outline-success"
          onClick={() =>
            printOrDownloadBulk(getFilteredOrders("shipped"), true)
          }
        >
          PDF Shipped
        </button>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body">
          <ReusableTable columns={columns} data={filtered} title="Order List" />
        </div>
      </div>

      {selectedOrder && (
        <div
          className="modal show fade d-block"
          style={{ background: "rgba(0,0,0,.5)" }}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="modal-dialog modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content p-3">
              <div ref={invoiceRef} className="p-4 bg-white">
                <div
                  dangerouslySetInnerHTML={{
                    __html: renderOrderHTML(selectedOrder),
                  }}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedOrder(null)}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => window.print()}
                >
                  Print
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={async () => {
                    await new Promise((r) => setTimeout(r, 300)); // wait for images

                    const canvas = await html2canvas(invoiceRef.current, {
                      scale: 1.6,
                      useCORS: true,
                    });

                    const img = canvas.toDataURL("image/jpeg", 0.72);

                    const pdf = new jsPDF({
                      orientation: "p",
                      unit: "mm",
                      format: "a4",
                      compress: true,
                    });

                    const width = pdf.internal.pageSize.getWidth();
                    const height = (canvas.height * width) / canvas.width;

                    pdf.addImage(img, "JPEG", 0, 0, width, height);
                    pdf.save(`Order-${selectedOrder.merchantOrderId}.pdf`);
                  }}
                >
                  PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ordermanagement;
